import { useState, type CSSProperties } from "react";

const API_BASE = "https://qstrades-api.aizo.workers.dev/api";
const LANDING_URL = "https://qstrades.io";
const TRIAL_URL = "https://qstrades.io/#pricing";

const DASHBOARD_VALUE_POINTS = [
  "Active futures signals across ES, NQ, GC, CL, and HG",
  "Confidence, entry, stop, target, and risk/reward in one place",
  "Live market context, model forecasts, order flow, and recent results",
];

const pageStyle: CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#0f172a",
  fontFamily: "Inter, system-ui, sans-serif",
  padding: "32px 0",
  boxSizing: "border-box",
};

const cardStyle: CSSProperties = {
  background: "#1e293b",
  borderRadius: 16,
  padding: "clamp(32px, 6vw, 48px) clamp(24px, 6vw, 48px)",
  maxWidth: 520,
  width: "90%",
  boxSizing: "border-box",
  boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
};

const headerStyle: CSSProperties = { textAlign: "center", marginBottom: 32 };
const titleStyle: CSSProperties = { color: "#f8fafc", fontSize: 28, fontWeight: 800, margin: 0 };
const subtitleStyle: CSSProperties = { color: "#cbd5e1", marginTop: 8, fontSize: 14, lineHeight: 1.5 };
const valueBoxStyle: CSSProperties = {
  marginTop: 20,
  padding: "14px 16px",
  borderRadius: 12,
  background: "rgba(15, 23, 42, 0.62)",
  border: "1px solid #334155",
  textAlign: "left",
};
const valueKickerStyle: CSSProperties = { color: "#cbd5e1", fontSize: 13, fontWeight: 700, marginBottom: 8 };
const valueListStyle: CSSProperties = { margin: 0, paddingLeft: 18, color: "#cbd5e1", fontSize: 12, lineHeight: 1.75 };
const fieldGroupStyle: CSSProperties = { marginBottom: 16 };
const passwordGroupStyle: CSSProperties = { marginBottom: 24 };
const fieldLabelStyle: CSSProperties = { color: "#cbd5e1", fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 };
const fieldInputStyle: CSSProperties = {
  width: "100%",
  padding: "12px 16px",
  background: "#0f172a",
  border: "1.5px solid #334155",
  borderRadius: 8,
  color: "#f8fafc",
  fontSize: 15,
  outline: "none",
  boxSizing: "border-box",
};
const recoveryHintStyle: CSSProperties = { color: "#64748b", fontSize: 12, marginTop: 8, lineHeight: 1.5 };
const actionAreaStyle: CSSProperties = { marginTop: 24, textAlign: "center" };
const mutedTextStyle: CSSProperties = { color: "#64748b", fontSize: 13 };
const linkButtonStyle: CSSProperties = {
  background: "none",
  border: "none",
  color: "#60a5fa",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 600,
  padding: "6px 8px",
};
const recoveryLinksStyle: CSSProperties = { display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap" };
const registerHintStyle: CSSProperties = { color: "#475569", fontSize: 12, marginTop: 12 };
const footerLinksStyle: CSSProperties = { display: "flex", justifyContent: "center", gap: 16, flexWrap: "wrap", marginTop: 18 };
const landingLinkStyle: CSSProperties = { color: "#cbd5e1", fontSize: 13, fontWeight: 600, textDecoration: "none", padding: "6px 8px" };
const trialLinkStyle: CSSProperties = { color: "#60a5fa", fontSize: 13, fontWeight: 600, textDecoration: "none", padding: "6px 8px" };
const successAlertStyle: CSSProperties = {
  background: "#052e16",
  border: "1px solid #15803d",
  borderRadius: 8,
  padding: 12,
  marginBottom: 20,
  color: "#86efac",
  fontSize: 14,
};
const errorAlertStyle: CSSProperties = {
  background: "#451a03",
  border: "1px solid #92400e",
  borderRadius: 8,
  padding: 12,
  marginBottom: 20,
  color: "#fbbf24",
  fontSize: 14,
};

function submitButtonStyle(loading: boolean): CSSProperties {
  return {
    width: "100%",
    padding: 14,
    background: loading ? "#334155" : "#2563eb",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 600,
    cursor: loading ? "not-allowed" : "pointer",
    transition: "background 0.2s",
  };
}

type LoginMode = "login" | "register" | "forgot" | "forgotEmail" | "reset";

const MODE_CONTENT: Record<LoginMode, { subtitle: string; submitLabel: string }> = {
  login: {
    subtitle: "Sign in for live signals, market context, model forecasts, and recent results.",
    submitLabel: "Sign In",
  },
  register: {
    subtitle: "Create your account to connect your active subscription.",
    submitLabel: "Create Account",
  },
  forgot: {
    subtitle: "Reset your password by email",
    submitLabel: "Send Reset Link",
  },
  forgotEmail: {
    subtitle: "Recover your dashboard email",
    submitLabel: "Request Email Recovery",
  },
  reset: {
    subtitle: "Choose a new password",
    submitLabel: "Reset Password",
  },
};

function authRequestForMode(
  mode: LoginMode,
  fields: { email: string; password: string; recoveryContact: string; resetToken: string },
) {
  switch (mode) {
    case "login":
      return { endpoint: "/login", body: { email: fields.email, password: fields.password } };
    case "register":
      return { endpoint: "/register", body: { email: fields.email, password: fields.password } };
    case "forgot":
      return { endpoint: "/forgot-password", body: { email: fields.email } };
    case "forgotEmail":
      return { endpoint: "/forgot-email", body: { contact: fields.recoveryContact } };
    case "reset":
      return { endpoint: "/reset-password", body: { token: fields.resetToken, password: fields.password } };
  }
}

function needsEmail(mode: LoginMode) {
  return mode !== "reset" && mode !== "forgotEmail";
}

function needsPassword(mode: LoginMode) {
  return mode !== "forgot" && mode !== "forgotEmail";
}

function needsStrongPassword(mode: LoginMode) {
  return mode === "register" || mode === "reset";
}

function showsDashboardValuePoints(mode: LoginMode) {
  return mode === "login" || mode === "register";
}

interface LoginPageProps {
  onLogin: (user: { email: string; role: string }, token: string) => void;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const initialResetToken = new URLSearchParams(window.location.search).get("reset") || "";
  const [mode, setMode] = useState<LoginMode>(initialResetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryContact, setRecoveryContact] = useState("");
  const [resetToken] = useState(initialResetToken);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const clearFeedback = () => {
    setError("");
    setMessage("");
  };

  const switchMode = (nextMode: LoginMode) => {
    setMode(nextMode);
    clearFeedback();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearFeedback();
    setLoading(true);

    try {
      const { endpoint, body } = authRequestForMode(mode, { email, password, recoveryContact, resetToken });
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMessage = data.error || "Authentication failed";
        if (mode === "register" && errorMessage.toLowerCase().includes("account already exists")) {
          setMode("login");
          setError("Account already exists — sign in with your existing password.");
        } else {
          setError(errorMessage);
        }
        return;
      }

      if (mode === "forgot") {
        setMessage("If that email has a dashboard account, a reset link has been sent.");
        return;
      }

      if (mode === "forgotEmail") {
        setMessage("Recovery request sent. We'll verify your identity before sharing the dashboard email.");
        return;
      }

      localStorage.setItem("qst_token", data.token);
      localStorage.setItem("qst_user", JSON.stringify(data.user));
      if (mode === "reset") {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      onLogin(data.user, data.token);
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>
            QS Trades Dashboard
          </h1>
          <p style={subtitleStyle}>
            {MODE_CONTENT[mode].subtitle}
          </p>
          {showsDashboardValuePoints(mode) && (
            <div style={valueBoxStyle}>
              <div style={valueKickerStyle}>
                Telegram alerts for speed. Dashboard for context and accountability.
              </div>
              <ul style={valueListStyle}>
                {DASHBOARD_VALUE_POINTS.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {message && (
          <div style={successAlertStyle}>
            {message}
          </div>
        )}

        {error && (
          <div style={errorAlertStyle}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {needsEmail(mode) && (
            <div style={fieldGroupStyle}>
              <label htmlFor="dashboard-email" style={fieldLabelStyle}>
                Email
              </label>
              <input
                id="dashboard-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                required
                style={fieldInputStyle}
              />
            </div>
          )}

          {mode === "forgotEmail" && (
            <div style={fieldGroupStyle}>
              <label htmlFor="dashboard-recovery-contact" style={fieldLabelStyle}>
                Telegram username, phone, or contact method
              </label>
              <input
                id="dashboard-recovery-contact"
                type="text"
                value={recoveryContact}
                onChange={(e) => setRecoveryContact(e.target.value)}
                placeholder="@username or phone"
                required
                style={fieldInputStyle}
              />
              <p style={recoveryHintStyle}>
                We verify against Stripe/Telegram before sharing account emails.
              </p>
            </div>
          )}

          {needsPassword(mode) && (
            <div style={passwordGroupStyle}>
              <label htmlFor="dashboard-password" style={fieldLabelStyle}>
                {mode === "reset" ? "New password" : "Password"}
              </label>
              <input
                id="dashboard-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={needsStrongPassword(mode) ? "8+ characters" : "Your password"}
                required
                minLength={needsStrongPassword(mode) ? 8 : undefined}
                style={fieldInputStyle}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={submitButtonStyle(loading)}
          >
            {loading ? "Please wait..." : MODE_CONTENT[mode].submitLabel}
          </button>
        </form>

        <div style={actionAreaStyle}>
          <p style={mutedTextStyle}>
            {mode === "login" ? (
              <>Don't have an account?{" "}
                <button
                  onClick={() => switchMode("register")}
                  style={linkButtonStyle}
                >
                  Register
                </button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button
                  onClick={() => switchMode("login")}
                  style={linkButtonStyle}
                >
                  Sign In
                </button>
              </>
            )}
          </p>
          {mode === "login" && (
            <div style={recoveryLinksStyle}>
              <button
                onClick={() => switchMode("forgot")}
                style={linkButtonStyle}
              >
                Forgot password?
              </button>
              <button
                onClick={() => switchMode("forgotEmail")}
                style={linkButtonStyle}
              >
                Forgot email?
              </button>
            </div>
          )}
          {mode === "register" && (
            <p style={registerHintStyle}>
              You must have an active QS Trades subscription to register.
            </p>
          )}
          <div style={footerLinksStyle}>
            <a
              href={LANDING_URL}
              style={landingLinkStyle}
            >
              ← Back to QS Trades
            </a>
            <a
              href={TRIAL_URL}
              style={trialLinkStyle}
            >
              Start trial →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
